import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import TrackLeaderboard from '@/components/telemetry/TrackLeaderboard';
import VenueDisplayHeader from '@/components/venue/VenueDisplayHeader';
import { TRACK_BOARDS, trackBoardByKey } from '@/lib/trackBoards';

// How long each circuit board stays on screen before the next one fades in.
const ROTATE_MS = 13000;

/**
 * Wall-mounted leaderboard display.
 *
 * Public and chrome-free: it renders outside the site layout, so no navbar,
 * ticker, footer or cookie banner ever mounts. By default it rotates through
 * every circuit so the screen runs hands-free; passing ?track=<key> pins a
 * single circuit to a specific monitor and stops the rotation.
 */
export default function VenueLeaderboard() {
  const [searchParams] = useSearchParams();
  const pinned = trackBoardByKey(searchParams.get('track'));
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (pinned) return;
    let timer;
    const start = () => {
      timer = setInterval(() => setIndex((i) => (i + 1) % TRACK_BOARDS.length), ROTATE_MS);
    };
    const stop = () => clearInterval(timer);
    // A hidden tab isn't being watched, so don't burn through cycles while away.
    const onVisibility = () => { stop(); if (!document.hidden) start(); };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [pinned]);

  const board = pinned || TRACK_BOARDS[index % TRACK_BOARDS.length];

  return (
    <div className="min-h-screen bg-[#0d0d0f] text-white flex flex-col">
      <VenueDisplayHeader
        title="Live Leaderboard"
        subtitle={pinned ? pinned.title : 'All circuits · rotating'}
      />

      <main className="flex-1 px-6 lg:px-10 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={board.trackKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: 'easeInOut' }}
          >
            <TrackLeaderboard {...board} />
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}